/**
 * Atlas PM · 任务详情（原型 js/detail.js）· 抽屉头 .dh
 *
 * 对齐原型结构：
 *   .r1  状态点 + 任务名 + 项目 / 主责 / 分类标签 / 计划区间 / 逾期 + 关闭
 *   .r2  状态徽标 + 审批通过徽标 + 优先级 + 任务编号
 *   .progwrap  系统计算进度（进度是算出来的，不是填的）
 */

import Badge from '../../components/Badge';
import { priorityLabel, priorityTone } from '@atlaspm/shared';
import type { ApprovalInstanceInfo, TaskDetail, TaskProgressInfo } from '@atlaspm/shared';
import type { EditPolicy } from './useTaskDetail';
import { stateMeta } from './useTaskDetail';

export interface DetailHeaderProps {
  task: TaskDetail;
  progress?: TaskProgressInfo;
  approval?: ApprovalInstanceInfo | null;
  policy: EditPolicy;
  onClose: () => void;
}

function fmtDate(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function overdueDays(task: TaskDetail): number {
  if (!task.planEnd || ['FROZEN', 'ARCHIVED', 'CANCELLED'].includes(task.state)) return 0;
  const end = Date.parse(task.planEnd);
  if (Number.isNaN(end)) return 0;
  /* P7.8：按自然日计算（忽略时分秒）。后端 plan_end 为 UTC 零点，
     本地今天也取 UTC 零点，二者同坐标系，避免东八区差一天。 */
  const n = new Date();
  const today = Date.UTC(n.getFullYear(), n.getMonth(), n.getDate());
  const diff = today - end;
  return diff > 0 ? Math.floor(diff / 86_400_000) : 0;
}

export default function DetailHeader({
  task,
  progress,
  approval,
  policy,
  onClose,
}: DetailHeaderProps) {
  const meta = stateMeta(task.state);
  const pct = progress?.progressPct ?? task.progressPct ?? 0;
  const overdue = overdueDays(task);
  const approved = policy.isFrozen || Boolean(task.frozenAt);
  const currentNode =
    (approval?.nodes ?? []).find((n) => n.status === 'IN_PROGRESS') ?? null;

  return (
    <header className="dh">
      <div className="r1">
        <span className="d" style={{ background: meta.color }} />
        <div className="nm">
          <h2>{task.name}</h2>
          <div className="sub">
            <span>{task.projectName ?? '—'}</span>
            <span>·</span>
            <span>{task.ownerName ?? '未指派'}</span>
            {task.categoryName && <span className="tag">{task.categoryName}</span>}
            <span>
              {fmtDate(task.planStart)} → {fmtDate(task.planEnd)}
            </span>
            {overdue > 0 && <span className="overdue">逾期 {overdue} 天</span>}
          </div>
        </div>
        {/* ⭐ P7.7：编辑 / 删除入口已从顶部移到底部操作区（TaskDrawer <footer>），
            避免重复入口；此处只保留关闭按钮。 */}
        <button type="button" className="close" aria-label="关闭" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="r2">
        <Badge tone={meta.tone}>
          {meta.icon} {meta.label}
        </Badge>
        {approved && (
          <Badge tone="ok">
            ✓ 审批通过 · 第 {task.freezeVersion || 1} 版
          </Badge>
        )}
        {task.state === 'REJECTED' && <Badge tone="alert">✕ 已驳回，待修改重报</Badge>}
        {currentNode && <Badge tone="warn">审批中 · {currentNode.name}</Badge>}
        <Badge tone={priorityTone(task.priority)}>优先级 {priorityLabel(task.priority)}</Badge>
        <span className="code">任务编号 {task.code}</span>
      </div>

      <div className="progwrap">
        <span className="pl">系统计算进度</span>
        <span className="pb">
          <i
            style={{
              width: `${Math.max(0, Math.min(100, pct))}%`,
              background: meta.color,
            }}
          />
        </span>
        <span className="pv num">{pct.toFixed(1)}%</span>
      </div>
    </header>
  );
}
